import {
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSettingsContext } from 'src/components/settings';
import usePagination from 'src/hooks/usePagination';
import getPaymentSummary from 'src/services/payment-summary/getPaymentSummary';
import useColumn from './hook/useColumn';
import CustomTable from 'src/components/CustomTable';
import { DatePicker } from '@mui/x-date-pickers';
import CustomPagination from 'src/components/CustomPagination';

export default function PaymentSummaryPage() {
    const { themeStretch } = useSettingsContext();
    const { column } = useColumn();

    const {
        page,
        page_size,
        handlePagination,
        setPage,
        handlePageSize,
        setTotal_records,
        setTotalPages,
        totalPages,
        total_records

    } = usePagination();

    const [date, setDate] = useState(dayjs());
    const [value, setValue] = useState('event');
    const [counts, setCounts] = useState({});
    const [rows, setRows] = useState([]);

    /** ✅ SAFE formatter (prevents NaN & undefined issues) */
    const formatAmount = (value) => Number(value || 0).toFixed(2);

    const handleChange = (_, newValue) => {
        setValue(newValue);
    };

    /** Fetch summary cards */
    useEffect(() => {
        const fetchSummary = async () => {
            const from_date = dayjs(date).startOf('day').format();
            const till_date = dayjs(date).endOf('day').format();

            const response = await getPaymentSummary(
                'summery',
                0,
                15,
                from_date,
                till_date
            );

            setCounts(response || {});
        };

        fetchSummary();
    }, [date]);

    /** Fetch table data */
    const [loading, setLoading] = useState(false)
    useEffect(() => {
        const fetchTableData = async () => {
            try {
                const from_date = dayjs(date).startOf('day').format();
                const till_date = dayjs(date).endOf('day').format();
                setLoading(true)
                const response = await getPaymentSummary(
                    value,
                    page,
                    page_size,
                    from_date,
                    till_date
                );

                setRows(response?._payload || []);
                setTotalPages(response?.totalPages || 1);
                setTotal_records(response?.total_records || rows?.length);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false)
            }
        };
        if (new Date(dayjs(date.startOf("day"))) < new Date(dayjs().startOf("day"))) {
            fetchTableData();
        }
    }, [date, value, page, page_size]);

    return (
        <Container maxWidth={themeStretch ? false : 'lg'}>
            {/* Header */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ mb: 1 }}
            >
                <Typography variant="h4">Payment Summary</Typography>

                <DatePicker
                    value={dayjs(date)}
                    format='DD/MM/YYYY'
                    disableFuture
                    onChange={(newDate) => setDate(newDate)}
                />
            </Stack>

            {/* Summary Cards */}
            <Grid container spacing={1}>
                <Grid item xs={12} md={6}>

                    <Paper sx={{ padding: '20px 10px' }}>
                        <Stack alignItems="center">
                            <Typography variant="h5" color="primary">
                                ₹ {formatAmount(
                                    counts?.event_payments?.total_event_revenue
                                    + (counts?.other_payments?.total_billing_amount || 0)
                                    + (counts?.other_payments?.total_topups || 0)
                                )}
                            </Typography>
                            <Typography variant="body1">Total Sales Amount</Typography>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ padding: '20px 10px' }}>
                        <Stack alignItems="center">
                            <Typography variant="h5" color="primary">
                                ₹ {formatAmount(counts?.other_payments?.total_billing_amount)}
                            </Typography>
                            <Typography variant="body1">Others, Included GST ₹{(counts?.other_payments?.total_gst_amount || 0)}</Typography>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Paper sx={{ padding: '20px 10px' }}>
                        <Stack alignItems="center">
                            <Typography variant="h5" color="primary">
                                ₹ {formatAmount((counts?.event_payments?.total_ticket_amount || 0) + (counts?.event_payments?.total_topups || 0))}
                            </Typography>
                            <Typography variant="body1">Event / Ticket Sales</Typography>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Paper sx={{ padding: '20px 10px' }}>
                        <Stack alignItems="center">
                            <Typography variant="h5" color="primary">
                                ₹ {formatAmount(counts?.event_payments?.total_platform_fee)}
                            </Typography>
                            <Typography variant="body1">Platform Fee Collected</Typography>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Paper sx={{ padding: '20px 10px' }}>
                        <Stack alignItems="center">
                            <Typography variant="h5" color="primary">
                                ₹ {formatAmount(counts?.event_payments?.total_gst)}
                            </Typography>
                            <Typography variant="body1">GST on Fee</Typography>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Tabs */}
            {new Date(dayjs(date.startOf("day"))) < new Date(dayjs().startOf("day")) && <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 2 }}>
                <Tabs value={value} onChange={handleChange}>
                    <Tab label="Event Data" value="event" />
                    <Tab label="Credit Purchase" value="credit" />
                    <Tab label="Other Payouts" value="invoice" />
                </Tabs>
            </Box>}

            {/* Table */}
            {new Date(dayjs(date.startOf("day"))) < new Date(dayjs().startOf("day")) && <CustomTable
                columns={column[value]}
                loading={loading}
                rows={rows}
            />}

            {rows?.length ? <CustomPagination page={page} page_size={page_size} total_records={total_records} setPage={setPage} totalPages={totalPages} handlePageSize={handlePageSize} /> : null}

        </Container>
    );
}
